import { Link } from 'react-router-dom';
import {
  Building2,
  CheckCircle,
  Shield,
  Clock,
  DollarSign,
  Users,
  FileText,
  Wrench,
  BarChart3,
  Bell,
  Smartphone,
  ArrowRight,
  Star,
  ChevronRight,
  Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-lg">
                <Building2 className="h-6 w-6" />
              </div>
              <span className="text-xl font-bold text-gray-900">Letops</span>
            </Link>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-gray-900">Features</a>
              <a href="#pricing" className="text-sm font-medium text-gray-600 hover:text-gray-900">Pricing</a>
              <a href="#testimonials" className="text-sm font-medium text-gray-600 hover:text-gray-900">Testimonials</a>
              <a href="#faq" className="text-sm font-medium text-gray-600 hover:text-gray-900">FAQ</a>
            </div>

            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="hidden sm:flex">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Get Started Free</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 via-white to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Star className="h-4 w-4 fill-current" />
              #1 Property Management Platform in Atlantic Canada
            </div>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Property Management
              <span className="text-blue-600"> Made Simple</span>
            </h1>
            
            <p className="mt-6 text-xl text-gray-600 max-w-2xl mx-auto">
              Streamline rent collection, maintenance requests, and tenant communication. 
              Everything you need to manage your rental properties in one powerful platform.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6 h-auto gap-2">
                  Start Free Trial
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" className="text-lg px-8 py-6 h-auto gap-2">
                <Play className="h-5 w-5" />
                Watch Demo
              </Button>
            </div>

            <p className="mt-4 text-sm text-gray-500">
              No credit card required • Free 14-day trial • Cancel anytime
            </p>
          </div>

          {/* Hero Image/Dashboard Preview */}
          <div className="mt-16 relative">
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10 pointer-events-none" />
            <div className="rounded-2xl shadow-2xl border border-gray-200 overflow-hidden bg-gray-900">
              <div className="bg-gray-800 px-4 py-3 flex items-center gap-2">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="flex-1 text-center">
                  <span className="text-xs text-gray-400">pm.bridgetrade.ca</span>
                </div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-8 min-h-[400px] flex items-center justify-center">
                <div className="text-center">
                  <Building2 className="h-24 w-24 text-blue-200 mx-auto mb-4" />
                  <p className="text-gray-400">Dashboard Preview</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-12 bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">500+</p>
              <p className="text-sm text-gray-500">Properties Managed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">98%</p>
              <p className="text-sm text-gray-500">On-time Rent Collection</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">24h</p>
              <p className="text-sm text-gray-500">Avg. Response Time</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">4.9/5</p>
              <p className="text-sm text-gray-500">Customer Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Everything You Need to Manage Properties
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              From rent collection to maintenance requests, Letops handles it all so you can focus on growing your portfolio.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard
              icon={DollarSign}
              title="Rent Collection"
              description="Automated rent reminders, online payments, and real-time tracking. Never chase a late payment again."
            />
            <FeatureCard
              icon={Wrench}
              title="Maintenance Management"
              description="Streamlined work orders with contractor dispatch, status tracking, and tenant communication."
            />
            <FeatureCard
              icon={Users}
              title="Tenant Portal"
              description="Self-service portal for tenants to pay rent, submit requests, and access documents 24/7."
            />
            <FeatureCard
              icon={FileText}
              title="Lease Management"
              description="Digital lease creation, e-signatures, and automatic renewals. NS Form P compliant."
            />
            <FeatureCard
              icon={BarChart3}
              title="Financial Reports"
              description="Real-time dashboards, income statements, and tax-ready reports at your fingertips."
            />
            <FeatureCard
              icon={Bell}
              title="Smart Notifications"
              description="Stay informed with instant alerts for payments, maintenance updates, and lease expirations."
            />
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">
                Save 10+ Hours Every Week
              </h2>
              <p className="mt-4 text-lg text-blue-100">
                Automate repetitive tasks and focus on what matters most - growing your rental business.
              </p>
              
              <div className="mt-8 space-y-4">
                <BenefitItem text="Automated rent reminders reduce late payments by 60%" />
                <BenefitItem text="Digital lease signing saves 2 hours per tenant" />
                <BenefitItem text="Maintenance tracking cuts response time in half" />
                <BenefitItem text="One-click financial reports for tax season" />
              </div>

              <Link to="/register" className="inline-block mt-8">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 gap-2">
                  Start Saving Time
                  <ArrowRight className="h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                    <Clock className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">10+ hours</p>
                    <p className="text-blue-200">Saved per week</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                    <DollarSign className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">$500+</p>
                    <p className="text-blue-200">Saved monthly on admin</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/20">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">100%</p>
                    <p className="text-blue-200">NS compliance guaranteed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Simple, Transparent Pricing
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              No hidden fees. No long-term contracts. Just powerful property management.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <PricingCard
              name="Starter"
              price="Free"
              description="Perfect for getting started"
              features={[
                "Up to 3 properties",
                "Basic rent tracking",
                "Tenant management",
                "Email support",
              ]}
            />
            <PricingCard
              name="Professional"
              price="$29"
              period="/month"
              description="For growing landlords"
              features={[
                "Up to 25 properties",
                "Online rent collection",
                "Maintenance management",
                "Financial reports",
                "Tenant portal",
                "Priority support",
              ]}
              highlighted
            />
            <PricingCard
              name="Enterprise"
              price="Custom"
              description="For property managers"
              features={[
                "Unlimited properties",
                "White-label option",
                "API access",
                "Dedicated account manager",
                "Custom integrations",
                "SLA guarantee",
              ]}
            />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Loved by Landlords Across Atlantic Canada
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <TestimonialCard
              quote="Letops has transformed how I manage my properties. Rent collection is now automatic and I save hours every week."
              author="Sarah M."
              role="Landlord, Halifax"
              rating={5}
            />
            <TestimonialCard
              quote="The maintenance tracking alone is worth it. My tenants love being able to submit requests online and track progress."
              author="Michael C."
              role="Property Manager, Dartmouth"
              rating={5}
            />
            <TestimonialCard
              quote="Finally, a property management tool built for Nova Scotia landlords. The Form P lease templates are a game changer."
              author="Jennifer L."
              role="Landlord, Bedford"
              rating={5}
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            <FAQItem
              question="Is Letops compliant with Nova Scotia tenancy laws?"
              answer="Yes! Letops is built specifically for Atlantic Canadian landlords. Our lease templates follow NS Form P requirements, and we stay up-to-date with all provincial regulations including the 5% rent cap."
            />
            <FAQItem
              question="Can tenants pay rent online?"
              answer="Absolutely. Tenants can pay via credit card, debit, or bank transfer through our secure tenant portal. Payments are deposited directly to your bank account."
            />
            <FAQItem
              question="How does maintenance management work?"
              answer="Tenants submit requests through the portal. You can assign contractors, track progress, and communicate with everyone involved. We also integrate with local contractor networks for quick dispatch."
            />
            <FAQItem
              question="Is my data secure?"
              answer="Yes. We use bank-level encryption, secure Canadian data centers, and follow strict privacy protocols. Your data is never shared with third parties."
            />
            <FAQItem
              question="Can I try before I buy?"
              answer="Of course! Start with our free plan for up to 3 properties, or try Professional free for 14 days. No credit card required."
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white">
            Ready to Simplify Property Management?
          </h2>
          <p className="mt-4 text-xl text-blue-100">
            Join hundreds of landlords who've already made the switch to Letops.
          </p>
          
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/register">
              <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8 py-6 h-auto gap-2">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" className="text-white border-white/30 hover:bg-white/10 text-lg px-8 py-6 h-auto">
              Schedule a Demo
            </Button>
          </div>

          <p className="mt-6 text-blue-200 text-sm">
            Free forever for up to 3 properties • No credit card required
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <Building2 className="h-6 w-6" />
                </div>
                <span className="text-xl font-bold text-white">Letops</span>
              </div>
              <p className="text-sm">
                Property management software built for Atlantic Canadian landlords.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
                <li><a href="#" className="hover:text-white">API</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
                <li><a href="#" className="hover:text-white">Careers</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white">Terms of Service</a></li>
                <li><a href="#" className="hover:text-white">Cookie Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm">© 2025 Letops. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <Smartphone className="h-5 w-5" />
              <span className="text-sm">Available on iOS & Android (Coming Soon)</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Feature Card Component
function FeatureCard({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-lg transition-shadow">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-blue-600 mb-4">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  );
}

// Benefit Item Component
function BenefitItem({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-3">
      <CheckCircle className="h-5 w-5 text-blue-200 flex-shrink-0" />
      <span className="text-white">{text}</span>
    </div>
  );
}

// Pricing Card Component
function PricingCard({ 
  name, 
  price, 
  period, 
  description, 
  features, 
  highlighted 
}: { 
  name: string; 
  price: string; 
  period?: string; 
  description: string; 
  features: string[]; 
  highlighted?: boolean;
}) {
  return (
    <div className={`rounded-2xl p-8 ${highlighted ? 'bg-blue-600 text-white ring-4 ring-blue-600 ring-offset-4' : 'bg-white border border-gray-200'}`}>
      <h3 className={`text-lg font-semibold ${highlighted ? 'text-white' : 'text-gray-900'}`}>{name}</h3>
      <p className={`text-sm mt-1 ${highlighted ? 'text-blue-100' : 'text-gray-500'}`}>{description}</p>
      
      <div className="mt-6">
        <span className={`text-4xl font-bold ${highlighted ? 'text-white' : 'text-gray-900'}`}>{price}</span>
        {period && <span className={highlighted ? 'text-blue-100' : 'text-gray-500'}>{period}</span>}
      </div>

      <ul className="mt-8 space-y-3">
        {features.map((feature, i) => (
          <li key={i} className="flex items-center gap-3">
            <CheckCircle className={`h-5 w-5 flex-shrink-0 ${highlighted ? 'text-blue-200' : 'text-blue-600'}`} />
            <span className={highlighted ? 'text-white' : 'text-gray-600'}>{feature}</span>
          </li>
        ))}
      </ul>

      <Link to="/register" className="block mt-8">
        <Button 
          className={`w-full ${highlighted ? 'bg-white text-blue-600 hover:bg-blue-50' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
        >
          Get Started
        </Button>
      </Link>
    </div>
  );
}

// Testimonial Card Component
function TestimonialCard({ quote, author, role, rating }: { quote: string; author: string; role: string; rating: number }) {
  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
      <div className="flex gap-1 mb-4">
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
        ))}
      </div>
      <p className="text-gray-600 mb-6">"{quote}"</p>
      <div>
        <p className="font-semibold text-gray-900">{author}</p>
        <p className="text-sm text-gray-500">{role}</p>
      </div>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <details className="group bg-white rounded-xl border border-gray-200 overflow-hidden">
      <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
        <span className="font-medium text-gray-900">{question}</span>
        <ChevronRight className="h-5 w-5 text-gray-400 group-open:rotate-90 transition-transform" />
      </summary>
      <div className="px-6 pb-6 text-gray-600">
        {answer}
      </div>
    </details>
  );
}
